import { fabric } from "fabric"
import Calc from "@/common/util/calculator"
import type Sign from "@/signomatic/app/sign"
import SignDrawables, { DrawableMode } from "@/signomatic/app/sign_drawables"
import type SignObjects from "@/signomatic/app/sign_objects"
import SignText from "@/signomatic/app/objects/sign_text"
import env from "@/common/env"

const slowPrinterFonts = [
  "Abril Fatface",
  "Amatic SC",
  "Cormorant",
  "Courier New",
  "Existence Light",
  "Exmouth",
  "Old Script",
  "Quicksand",
  "GeosansLight",
  "Playfair Display",
  "Apple Garamond",
]

export default class SignExport {
  private readonly sign: Sign
  private readonly objects: SignObjects

  constructor(sign: Sign, objects: SignObjects) {
    this.sign = sign
    this.objects = objects
  }

  async SVG(): Promise<string> {
    const canvas = this.createCanvas()
    await this.draw(canvas, false, false)
    const svg = canvas.toSVG()
    canvas.dispose()
    return svg
  }

  async engravingSVG(debug = false): Promise<string> {
    const canvas = this.createCanvas()
    await this.draw(canvas, true, debug)
    const svg = canvas.toSVG()
    canvas.dispose()
    return svg
  }

  private createCanvas() {
    const canvasEl = document.createElement("canvas")
    const instance = new fabric.StaticCanvas(canvasEl, {
      stateful: false,
      centeredScaling: true,
      enableRetinaScaling: false,
      renderOnAddRemove: false,
    })

    return instance
  }

  private centerX(canvas: fabric.StaticCanvas) {
    return canvas.getWidth() / 2
  }

  private centerY(canvas: fabric.StaticCanvas) {
    return canvas.getHeight() / 2
  }

  private async draw(canvas: fabric.StaticCanvas, engraving = false, debug = false, skipObjects = false) {
    const drawables = new SignDrawables(this.sign, engraving ? DrawableMode.Engraving : DrawableMode.Print, debug)

    let width = this.sign.state.widthPx
    let height = this.sign.state.heightPx
    const adjustment = drawables.engravingPadding
    const engravingOutsideExtra = drawables.engravingOutsideExtra
    const fixMargin = drawables.fixMethodMargin
    const printPadding = drawables.printPaddingWidth
    const fixTopOffset = drawables.externalTop && !drawables.externalBottom ? fixMargin.top / 2 : 0

    if (engraving) {
      width += adjustment + fixMargin.side + engravingOutsideExtra * 2
      height += adjustment + fixMargin.top + engravingOutsideExtra * 2
    } else {
      width += fixMargin.side + printPadding
      height += fixMargin.top + printPadding
    }

    canvas.clear()
    canvas.setWidth(width)
    canvas.setHeight(height)
    canvas.zoomToPoint(new fabric.Point(width / 2, height / 2), 1)
    canvas.calcOffset()

    if (env.isJSDOM) {
      await drawables.waitImagesLoaded()
    }

    const bases = drawables.baseDrawables()

    for (const base of bases) {
      if (!base) {
        continue
      }

      base.set("shadow", undefined)

      if (base.clipPath) {
        base.clipPath = engraving ? undefined : this.clipShape(canvas, fixTopOffset)
      }

      canvas.add(base)
      base.center()
      base.setCoords()

      if (base.offsetX) {
        base.set({ left: (base.left || 0) + base.offsetX })

        if (base.clipPath) {
          base.clipPath.set({ left: (base.clipPath.left || 0) + base.offsetX })
        }
      }

      base.set({ top: (base.top || 0) + base.offsetY + fixTopOffset })

      if (base.clipPath) {
        base.clipPath.set({
          top: (base.clipPath.top || 0) + drawables.clipOffsetY(base.offsetY),
        })
      }

      base.setCoords()
    }

    if (!skipObjects) {
      for (const obj of this.objects.cloneFromState(this.sign.state, this.centerX(canvas), this.centerY(canvas))) {
        if (!this.sign.state.doubleSided && obj.state.side && obj.state.side > 0) {
          continue
        }

        if (this.sign.state.doubleSided && obj.state.side !== this.sign.state.side) {
          continue
        }

        if (obj instanceof SignText) {
          obj.text = (obj.text || "").replace(/\u00a0/g, " ")

          const fontSize = parseFloat(Calc.pxToFontsize(obj.fontSize || 0).toFixed(1))

          if (engraving) {
            if (this.sign.signType === "contour_cut") {
              obj.stroke = drawables.engravingColor
              obj.strokeWidth = drawables.engravingStrokeWidth * 2
              obj.set("fill", "transparent")
            } else if (slowPrinterFonts.includes(obj.fontFamily || "") && fontSize <= 6) {
              if (this.sign.state.thickness === 0.8) {
                obj.set("fill", "rgb(0,150,0)")
              } else {
                obj.set("fill", "rgb(0,175,0)")
              }
            } else {
              obj.set("fill", drawables.engravingTextColor)
            }
          }

          if (slowPrinterFonts.includes(obj.fontFamily || "") && fontSize <= 6) {
            obj.addClass("slow-printer-font")
          }
        }

        obj.attachSign(this.sign)
        obj.addClass("object")

        if (this.sign.state.shape !== "contour") {
          obj.clipPath = this.clipShape(canvas, fixTopOffset, true)
        }

        obj.top = (obj.top || 0) + fixTopOffset
        obj.setCoords()

        if (obj.state.border && engraving) {
          obj.state.border.borderColor = drawables.engravingTextColor
        }

        canvas.add(obj)
      }
    }

    for (const top of drawables.topDrawables()) {
      if (!top) {
        continue
      }

      if (top.clipPath) {
        top.clipPath = this.clipShape(canvas, fixTopOffset)
      }

      canvas.add(top)
      top.center()
      top.setCoords()

      top.set({ top: (top.top || 0) + top.offsetY + fixTopOffset })
      top.setCoords()
    }

    canvas.renderAll()
  }

  private clipShape(canvas: fabric.StaticCanvas, topOffset?: number, object?: boolean) {
    const printAreaPadding = Calc.mmToPx(this.sign.printAreaPadding())
    const drawPrintAreaPadding = object && printAreaPadding > 0
    const clip = this.sign.clipShape(drawPrintAreaPadding)

    let left = canvas.getWidth() / 2
    let top = canvas.getHeight() / 2 + (topOffset || 0)

    if (drawPrintAreaPadding) {
      left = printAreaPadding * 2
      top = printAreaPadding * 2
    }

    if (clip) {
      clip.set({ left, top })
    }

    return clip
  }
}
